import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { newsItems } from "@/mocks/home";

gsap.registerPlugin(ScrollTrigger);

export default function NewsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector("[data-news-card]")?.clientWidth ?? 380;
    const gap = 20;
    const scrollAmount = cardWidth + gap;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const cards = cardsRef.current;
    if (!section || !header || !cards) return;

    const cardEls = cards.querySelectorAll("[data-news-card]");

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      header,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
    );

    tl.fromTo(
      cardEls,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, stagger: 0.12, ease: "power3.out" },
      "-=0.4"
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-background-50 py-20 md:py-28 lg:py-36"
    >
      {/* Header inside max-width container */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div
          ref={headerRef}
          className="flex items-end justify-between mb-10 md:mb-14"
        >
          <h2 className="font-heading font-bold uppercase text-[26px] sm:text-[34px] md:text-[44px] lg:text-[56px] text-foreground-950 tracking-tight leading-[0.92]">
            Aktuelles
            <br />&amp; Einblicke
          </h2>
          <div className="flex items-center gap-3 flex-shrink-0 ml-6">
            <button
              onClick={() => scroll("left")}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-foreground-200 flex items-center justify-center hover:border-accent-500 hover:bg-accent-500 group transition-all duration-300 cursor-pointer"
              aria-label="Vorherige News"
            >
              <i className="ri-arrow-left-line text-foreground-500 group-hover:text-white text-base md:text-lg w-4 h-4 md:w-5 md:h-5 flex items-center justify-center"></i>
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-11 h-11 md:w-12 md:h-12 rounded-full border-2 border-foreground-200 flex items-center justify-center hover:border-accent-500 hover:bg-accent-500 group transition-all duration-300 cursor-pointer"
              aria-label="Nächste News"
            >
              <i className="ri-arrow-right-line text-foreground-500 group-hover:text-white text-base md:text-lg w-4 h-4 md:w-5 md:h-5 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Cards overflow to the right edge */}
      <div ref={cardsRef}>
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-1 scrollbar-hide"
          style={{
            paddingLeft: "max(calc((100vw - 1400px) / 2 + 56px), 24px)",
            paddingRight: "max(calc((100vw - 1400px) / 2 + 56px), 24px)",
          }}
        >
          {newsItems.map((item, i) => (
            <article
              key={i}
              data-news-card
              className="flex-shrink-0 w-[340px] md:w-[400px] snap-start bg-background-100 rounded-2xl overflow-hidden group cursor-pointer transition-colors duration-300 hover:bg-background-200/80"
            >
              <div className="relative w-full h-48 md:h-52 overflow-hidden rounded-t-2xl bg-foreground-100">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-3">
                  <span
                    className={`text-xs font-label font-semibold uppercase px-3 py-1 rounded-full whitespace-nowrap ${
                      item.category === "Projekt"
                        ? "bg-accent-100 text-accent-800"
                        : "bg-accent-100 text-accent-800"
                    }`}
                  >
                    {item.category}
                  </span>
                  <span className="text-xs text-foreground-500 font-label whitespace-nowrap">
                    {item.date}
                  </span>
                </div>

                <h3 className="font-heading font-semibold text-foreground-950 text-base md:text-lg leading-snug line-clamp-3 mb-5 group-hover:text-foreground-700 transition-colors duration-300">
                  {item.title}
                </h3>

                <div className="flex justify-end">
                  <span className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 cursor-pointer">
                    <i className="ri-arrow-right-up-line text-white text-sm w-4 h-4 flex items-center justify-center"></i>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}